/**
 * StowedElement contract types – mirrors the protobuf schema.
 *
 * All API interactions for element CRUD must adhere to this structure.
 * The `kindId` indicates which element-type field is populated.
 *
 * Kind mapping:
 *   1 → rectangleElement
 *   2 → irregularPolygonElement
 *   5 → pageNumberElement
 *   6 → textOnPathElement
 *   7 → groupElement
 *   8 → ovalElement
 *   9 → polylineElement  (pen tool open/closed paths)
 *  10 → regularPolygonElement
 *
 * A text box is never written as a standalone element kind — the backend
 * retired that. It's persisted as a bare `kindId 1` Rectangle whose
 * `content.text` holds the paragraphs (see `buildStowedElement` in
 * `stowed-element-builder.ts`), the same pattern used for a placed photo
 * (`kindId 1` + `content.image`, never its own top-level "image" kind).
 * kindIds 3 and 4 are retired and must never be produced or expected again.
 */

// ── Base element ─────────────────────────────────────────────────────

export interface PageElement {
  x?: number
  y?: number
  width?: number
  height?: number
  rotation?: number
  geometricX?: number
  geometricY?: number
  geometricWidth?: number
  geometricHeight?: number
  visibleX?: number
  visibleY?: number
  visibleWidth?: number
  visibleHeight?: number
  anchorRef?: string
  uid?: number
  pagedness?: string
  transparency?: number
  isLocked?: boolean
  isFrozen?: boolean
  ignoreTextWrap?: boolean
  spotUv?: boolean
}

// ── Styles ───────────────────────────────────────────────────────────

export interface LineStyle {
  weight?: number
  colorId?: number
  patternId?: number
  tint?: number
}

export interface FillStyle {
  colorId?: number
  tint?: number
  gradient?: Gradient
}

export interface Gradient {
  startColor?: number
  endColor?: number
  gradientStyle?: string
}

export interface DropShadow {
  colorId?: number
  transparency?: number
  direction?: number
  offset?: number
}

export interface TextWrap {
  wrapStyle?: string
  wrapOffsetTop?: number
  wrapOffsetRight?: number
  wrapOffsetBottom?: number
  wrapOffsetLeft?: number
}

// ── Path ─────────────────────────────────────────────────────────────

export interface PathMoveTo {
  x?: number
  y?: number
}

export interface PathLineTo {
  x?: number
  y?: number
}

export interface PathCurveTo {
  controlX?: number
  controlY?: number
  /** Second control point (cubic Bézier). Maps to backend deltaX/deltaY. */
  controlX2?: number
  controlY2?: number
  x?: number
  y?: number
  /** Backend field for second control point (cubic). When loading, map to controlX2/controlY2. */
  deltaX?: number
  deltaY?: number
}

export interface PathCommand {
  moveTo?: PathMoveTo
  lineTo?: PathLineTo
  curveTo?: PathCurveTo
}

export interface Path {
  commands?: PathCommand[]
}

// ── Content ──────────────────────────────────────────────────────────

export interface TextRun {
  textAdvance?: number
  baseline?: number
  textWidth?: number
  fontId?: number
  fontFamilyId?: number
  pointSize?: number
  underline?: boolean
  strikethrough?: boolean
  textContent?: string
  kerning?: number
  tracking?: number
  leading?: string
  lineStyle?: LineStyle
  fillStyle?: FillStyle
  /**
   * Superscript/subscript (WMPCM-10141). Canvas-model-only: there is no legacy NSD/yb360
   * superscript or subscript wire field anywhere in this monorepo to mirror (checked
   * `larry-common-library/`, `larry-backend-api/`, `larry-load-api/`, and this file's own `proto/schema.proto` mirror — unlike
   * `TextOnPathElementData`, which has a real legacy XML field, this one does not exist yet
   * anywhere). `buildStowedElement`/`buildWireParagraphs` (`stowed-element-builder.ts`)
   * deliberately do NOT write this — an unknown field would just be silently dropped by the
   * backend on save (the same failure shape as Known Gaps #17/#40), so a page reload loses it.
   * A run stays raised/lowered for the rest of the session it was pasted or applied in, not
   * across a reload, until a backend field exists.
   */
  verticalAlign?: 'super' | 'sub'
}

export interface TextLine {
  leftMargin?: number
  rightMargin?: number
  textWidth?: number
  isHidden?: boolean
  descent?: number
  textRuns?: TextRun[]
}

export interface Paragraph {
  spaceAfter?: number
  spaceBefore?: number
  style?: number
  indentation?: number
  alignment?: number
  dropCap?: string
  dropCapLines?: number
  dropCapOffset?: number
  textLines?: TextLine[]
}

export interface Content {
  text?: TextElementData
  image?: ImageElementData
}

// ── Element types ────────────────────────────────────────────────────

export interface RectangleElementData {
  base?: PageElement
  roundedCorner?: number
  lineStyle?: LineStyle
  fillStyle?: FillStyle
  content?: Content
  dropShadow?: DropShadow
  textWrap?: TextWrap
  shapeImageFill?: ShapeImageFillData
}

export interface OvalElementData {
  base?: PageElement
  lineStyle?: LineStyle
  fillStyle?: FillStyle
  content?: Content
  dropShadow?: DropShadow
  textWrap?: TextWrap
}

export interface IrregularPolygonElementData {
  base?: PageElement
  path?: Path
  lineStyle?: LineStyle
  fillStyle?: FillStyle
  content?: Content
  shapeImageFill?: ShapeImageFillData
  /** Pending backend schema addition — see the Text Wrap implementation plan. Sent eagerly so no
   *  further frontend change is needed once the backend field exists; until then it round-trips
   *  through save but the backend drops it, so it won't survive a reload for this kind. */
  textWrap?: TextWrap
}

export interface PolylineElementData {
  base?: PageElement
  path?: Path
  lineStyle?: LineStyle
  /** True when the path is explicitly closed (Z command). Mirrors backend `is_closed` proto field. */
  isClosed?: boolean
  content?: Content
  shapeImageFill?: ShapeImageFillData
  /** Pending backend schema addition — see the Text Wrap implementation plan. */
  textWrap?: TextWrap
}

export interface RegularPolygonElementData {
  base?: PageElement
  /** Number of sides / star tips (3–100). */
  sides?: number
  /** Inset value (0-100) persisted via protobuf `inner`; string for legacy wire format. */
  inner?: string
  lineStyle?: LineStyle
  fillStyle?: FillStyle
  content?: Content
  shapeImageFill?: ShapeImageFillData
  /** Pending backend schema addition — see the Text Wrap implementation plan. */
  textWrap?: TextWrap
}

export interface TextElementData {
  paragraphs?: Paragraph[]
  /** Pending backend schema addition — see the Text Wrap implementation plan. */
  textWrap?: TextWrap
}

/**
 * Flat geometry — no nested `base` — matching the backend's `ImageElement` Go struct, which
 * embeds `x/y/width/height/rotation` directly rather than via a `PageElement` pointer.
 */
export interface ImageElementData {
  x?: number
  y?: number
  width?: number
  height?: number
  rotation?: number
  imageId?: string
  imageType?: string
  pezId?: string
  /** Preview URL persisted in protobuf for reload (canvas display URL — prefers urlScr). */
  imagePreviewUrl?: string
  /** Display name persisted in protobuf for reload. */
  imageDisplayName?: string
  /** Pending backend schema addition — see the Text Wrap implementation plan. */
  textWrap?: TextWrap
}

export interface PageNumberElementData {
  base?: PageElement
  leftRight?: string
  textRun?: TextRun
}

/**
 * Text on a path — NSD `<PE><TP>` (kindId 6), WMPCM-9542.
 *
 * Carries the text TWICE, deliberately, and both halves are required:
 *
 *  - `textLine` (`<TL>`) is the logical string plus its typography, in the same `TextRun` shape
 *    that shape text uses — so font, size, colour, tracking, kerning and the decoration flags all
 *    round trip. This is the half an editor reads back to reconstruct an editable model.
 *  - `characterContainer` (`<XT>` holding `<PX>` entries) is one entry per character with its
 *    baked position, rotation and advance along the path. This is the half yb360 draws from; it
 *    never re-flows the text itself.
 *
 * **Whitespace takes a `PX` entry of its own.** The two halves are index-aligned with the string,
 * so a writer that skipped spaces would shift every character after the first one.
 */
export interface TextOnPathElementData {
  base?: PageElement
  /** `MM` */
  margin?: number
  /** `SP` — distance along the path to the first character. */
  distance?: number
  /** `SS` — index of the path segment `distance` is measured from. */
  startSegment?: number
  /** `TO` — perpendicular offset from the path. */
  offset?: number
  /** `AN` — TOAP anchor flag. */
  anchor?: boolean
  /** `<TL>` — the whole string, with its typography. */
  textLine?: TextLine
  /** `<XT>` — per-character baked geometry, whitespace included. */
  characterContainer?: TextOnPathCharacterContainer
  /**
   * Backend schema skew compat, NOT part of the current wire contract — do not read this on a
   * fresh write of your own. Before commit `7b3c9419`, the Go model had no `characterContainer`
   * wrapper: this was a flat field of the same name directly on the element. `stowed-element-
   * builder.ts`'s `buildTextOnPathElement` mirrors `characterContainer.characters` here too, so a
   * backend build old enough to still unmarshal the flat shape receives the per-character data
   * under the name it understands; a current build ignores it (unmapped JSON key) and reads
   * `characterContainer` instead. `lib/elements/text-on-path-element.ts`'s `readTextOnPathConfig`
   * reads this as the fallback source of characters when `characterContainer` is absent. Drop this
   * field, the write side's mirror, and this comment once the live backend is confirmed to always
   * carry `characterContainer` (see the doc comment on `TextOnPathElementData` above and
   * `text-on-path-element.ts`'s "Backend schema skew").
   */
  characters?: TextOnPathCharacter[]
}

export interface TextOnPathCharacterContainer {
  characters?: TextOnPathCharacter[]
}

export interface TextOnPathCharacter {
  character?: string
  x?: number
  y?: number
  width?: number
  height?: number
  rotation?: number
  baseline?: number
  textAdvance?: number
}

export interface GroupElementData {
  base?: PageElement
  /** Text-wrap settings for the group as a single wrapping unit — see `buildTextWrap` in
   *  `stowed-element-builder.ts` and the group-aware read at `checkpoint-to-shapes.ts`. */
  textWrap?: TextWrap
}

// ── Photo fill (kindId 1, 2, 10) ─────────────────────────────────────

/**
 * Image fill applied to a non-image shape (kindId 1, 2, 10).
 * Nested inside the element-specific field (rectangleElement / irregularPolygonElement /
 * regularPolygonElement) as `shapeFillImage`. Mirrors the backend `ShapeImageFill` proto message.
 *
 * offsetX / offsetY: image offset within the scaled cover frame (pt). 0 = centered.
 * scale: additional zoom multiplier on top of auto-cover fit. 1.0 = auto.
 */
export interface ShapeImageFillData {
  imageId: string
  imageType: string
  pezId: string
  imagePreviewUrl?: string
  imageDisplayName?: string
  fillOffsetX?: number
  fillOffsetY?: number
  fillScale?: number
}

// ── Editor-specific metadata (round-tripped but not in protobuf) ────

export interface EditorShapeMetadata {
  type?: string
  fill?: string
  stroke?: string
  strokeWidth?: number
  opacity?: number
  inset?: number
  /** True when a pen-path (kindId=9) is closed so the Z is restored on reload. */
  isClosed?: boolean
  /** Mirrors `ShapeData.paragraphs`. */
  paragraphs?: Paragraph[]
}

// ── StowedElement (the contract) ─────────────────────────────────────
export interface CheckpointPayload {
  StowedElements: StowedElement[]
  linkedTextElements?: LinkedTextElement
}

export interface LinkedTextElement {
  textChain: TextChain[]
}

export interface TextChain {
  id: string
  layer: string
  textBox: TextBox[]
}

export interface TextBox {
  uid: string
}

export interface StowedElement {
  objectId: string
  /** Layer ordering key; required by Page Editor HTTP API for mutations. */
  fractionalIndex?: string
  versionId?: string
  kindId: number
  signedUrl?: string | null

  rectangleElement?: RectangleElementData
  ovalElement?: OvalElementData
  irregularPolygonElement?: IrregularPolygonElementData
  polylineElement?: PolylineElementData
  regularPolygonElement?: RegularPolygonElementData
  pageNumberElement?: PageNumberElementData
  textOnPathElement?: TextOnPathElementData
  groupElement?: GroupElementData

  elements?: StowedElement[]

  /**
   * Frontend-only metadata, sent on write but NOT persisted: the backend's
   * `document_models.StowedElement` (Go) has no `editorShape` field, and it does not appear in
   * `schema.proto`, so this key is silently dropped on write and is always absent from a decoded
   * checkpoint or a live GET. Only same-request round-trips (e.g. reading back a just-built
   * payload before it hits the wire) see it populated — never rely on it surviving a save/reload.
   * `checkpointToShapes` (`lib/checkpoint/checkpoint-to-shapes.ts`) has wire-durable fallbacks for
   * every field this carries (`lineStyle`, `fillStyle`, `inner`, path commands) for exactly this
   * reason. See `docs/text-unification.md` §2.3 for the full account of the bug this caused before
   * Phase 3 removed the text/shape discriminator that depended on it.
   */
  editorShape?: EditorShapeMetadata
}

// ── Kind → field-name mapping ────────────────────────────────────────

export const KIND_TO_FIELD: Record<number, keyof StowedElement> = {
  1: 'rectangleElement',
  2: 'irregularPolygonElement',
  5: 'pageNumberElement',
  6: 'textOnPathElement',
  7: 'groupElement',
  8: 'ovalElement',
  9: 'polylineElement',
  10: 'regularPolygonElement',
}

export type ElementLayer = 'userLayer' | 'masterLayer'
